"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { Button } from "../ui/button"
import { Footer } from "../layout/Footer"
import { getPlayerIconById } from "../../lib/playerIcons"
import { LocationCards, formatLocationName } from "./LocationCards"
import { SuspectsBoard } from "./SuspectsBoard"
import { socketService } from "../../services/socketService"
import { getRandomQuestionForDeck } from "../../lib/gameHelperQuestions"
import { deckImg } from "../../lib/utils"
import GoogleAdSquare from "../googleads/GoogleAdSquare"
import GoogleAdVertical from "../googleads/GoogleAdVertical"
import GoogleAdHorizontalFixed from "../googleads/GoogleAdHorizontalFixed"
import GoogleAdHorizontal from "../googleads/GoogleAdHorizontal"
import VotingPanel from "./VotingPanel"
import SpyGuessPanel from "./SpyGuessPanel"

interface PlayGameProps {
  isAdmin: boolean;
  playerName: string;
  playerId: string;
  role: string;
  location: string;
  isSpy: boolean;
  chosenCards: string[]; // The filtered/chosen cards for this game
  timerDuration: number;
  iconId?: number;
  currentDeck: string; // deck id
}



export default function PlayGame() {
  const location = useLocation();
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { isAdmin, playerName, role, location: gameLocation, isSpy, chosenCards = [], timerDuration = 480, iconId = 0, playerId, currentDeck } = location.state as PlayGameProps;
  

  // Track which cards have been crossed out
  const [crossedOutCards, setCrossedOutCards] = useState<Record<string, boolean>>({})
  const [timeLeft, setTimeLeft] = useState(timerDuration);
  const [progress, setProgress] = useState(100);
  const [scoreUpdated, setScoreUpdated] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<string>("");
  const [isRulesExpanded, setIsRulesExpanded] = useState(false);
  const [players, setPlayers] = useState<any[]>([]);
  const [roundKey, setRoundKey] = useState(0);
  const [gameRestartId, setGameRestartId] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isGameRestarting, setIsGameRestarting] = useState(false);
  const [lastPlayerUpdateHash, setLastPlayerUpdateHash] = useState('');
  const [currentPlayerScore, setCurrentPlayerScore] = useState(0);
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const [showMobileHint, setShowMobileHint] = useState(false);
  const [deadlineAtMs, setDeadlineAtMs] = useState<number | null>(null);
  const [votingCounts, setVotingCounts] = useState<Record<string, number>>({});
  const [votingVoters, setVotingVoters] = useState<string[]>([]);
  const [isVotingClosed, setIsVotingClosed] = useState(false);
  const [isSubmittingManualOutcome, setIsSubmittingManualOutcome] = useState(false);
  const [isSpyGuessPhase, setIsSpyGuessPhase] = useState(false);
  const isSpyGuessPhaseRef = useRef(false);
  const [spyGuessDeadline, setSpyGuessDeadline] = useState<number | null>(null);
  const [spyGuessTimeLeft, setSpyGuessTimeLeft] = useState(60);
  const [hasSubmittedSpyGuess, setHasSubmittedSpyGuess] = useState(false);
  const [spyGuessResult, setSpyGuessResult] = useState<{ correct: boolean; guessedLocation: string | null; actualLocation: string } | null>(null);
  const endRoundTriggeredRef = useRef(false);
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [roundResultText, setRoundResultText] = useState<JSX.Element | null>(null);

  // Debug logging to track currentDeck value
  useEffect(() => {
    console.log('PlayGame mounted with currentDeck:', currentDeck);
  }, [currentDeck]);

  // Mobile hint: show for 10s on mobile devices
  useEffect(() => {
    try {
      const isCoarse = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(pointer: coarse)').matches : false;
      const isSmall = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : false;
      const isMobileUA = typeof navigator !== 'undefined' ? /Mobi|Android/i.test(navigator.userAgent) : false;
      const isMobile = isCoarse || isSmall || isMobileUA;
      if (isMobile) {
        setShowMobileHint(true);
        const t = setTimeout(() => setShowMobileHint(false), 10000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  // Data-driven deck meta for images and labels (populated from backend deck definition)
  const [assetDir, setAssetDir] = useState<string>('');
  const [spyAssetName, setSpyAssetName] = useState<string>('');
  const [cardNoun, setCardNoun] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/decks/${currentDeck}`);
        if (res.ok) {
          const deck = await res.json();
          setAssetDir(deck.assetDir || '');
          setSpyAssetName(deck.spyAssetName || '');
          setCardNoun(deck.cardNoun || '');
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeck]);

  const fallbackAssetDir = currentDeck === 'animalsDeck' ? 'animals'
    : currentDeck === 'countriesDeck' ? 'countries'
    : 'locations';
  const resolvedAssetDir = assetDir || fallbackAssetDir;
  const resolvedSpyAssetName = spyAssetName || 'spy';
  const spyAssetNameRef = useRef(resolvedSpyAssetName);
  useEffect(() => { spyAssetNameRef.current = resolvedSpyAssetName; }, [resolvedSpyAssetName]);
  const cardNounRef = useRef(cardNoun);
  useEffect(() => { cardNounRef.current = cardNoun; }, [cardNoun]);
  const getImagePath = useCallback((item: string, isDeckItem: boolean = true) => {
    if (isDeckItem) {
      return deckImg(resolvedAssetDir, formatLocationName(item));
    }
    if (item === 'spy') {
      return `/imgs/${resolvedAssetDir}/${resolvedSpyAssetName}.webp`;
    }
    return `/imgs/${item}.webp`;
  }, [resolvedAssetDir, resolvedSpyAssetName]);

  // Reset image loading state when game location, deck changes, or game restarts
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    
    // Preload the main card image to ensure it's ready
    const preloadImage = (src: string) => {
      const img = new Image();
      img.onload = () => {
        console.log('Preloaded image:', src);
        setImageLoaded(true);
      };
      img.onerror = () => {
        console.error('Failed to preload image:', src);
        setImageError(true);
      };
      img.src = src;
    };

    if (!isSpy) {
      const imagePath = getImagePath(gameLocation, true);
      console.log('Player location image path:', imagePath);
      preloadImage(imagePath);
    } else {
      const spyImagePath = getImagePath('spy', false);
      console.log('Spy image path:', spyImagePath);
      preloadImage(spyImagePath);
    }

    chosenCards.forEach(card => {
      console.log('Chosen card image path:', getImagePath(card, true));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // This effect needs to run when the core game props change or when game restarts
  }, [isSpy, gameLocation, chosenCards, currentDeck, getImagePath, gameRestartId]);

  // Handle image loading events
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageLoadError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Failed to load main card image:', e.currentTarget.src);
    setImageError(true);
    setImageLoaded(false);
  };

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Toggle crossed out state
  const toggleCard = (location: string) => {
    setCrossedOutCards((prev) => ({
      ...prev,
      [location]: !prev[location],
    }))
  }

  const handleNewGame = () => {
    navigate('/app');
  };

  // Memoized handleEndRound function to prevent unnecessary re-renders
  const handleEndRound = useCallback(async () => {
    try {
      if (endRoundTriggeredRef.current) {
        return;
      }
      endRoundTriggeredRef.current = true;
      console.log('Ending round via REST API...');
      // Keep as hybrid (REST + Socket broadcast)
      const response = await fetch(`/api/rooms/${roomId}/end-round`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('API response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to end round: ${response.status} ${errorText}`);
      }
      console.log('Round ended successfully');
      // Timer will be set to 0 via socket event
    } catch (error) {
      console.error('Error ending round:', error);
      // Fallback: Set timer to 0 locally if socket event doesn't arrive
      const fallbackTimeout = setTimeout(() => {
        if (timeLeft > 0) {
          console.warn('Round end socket event not received - setting timer to 0 locally');
          setTimeLeft(0);
        }
      }, 2000);

      // Store timeout for cleanup
      fallbackTimeoutRef.current = fallbackTimeout;

      alert('Failed to end round properly. The round may still end via timer.');
    }
  }, [roomId, timeLeft]);

  // Timer effect based on server-anchored deadline
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | null = null;
    const tick = () => {
      if (!deadlineAtMs) return;
      const remaining = Math.ceil((deadlineAtMs - Date.now()) / 1000);
      const clamped = Math.max(0, remaining);
      setTimeLeft(clamped);
      setProgress((clamped / timerDuration) * 100);
      if (remaining <= 0 && id) {
        clearInterval(id);
        id = null;
        // Ensure end-round behavior kicks in if not already ended
        handleEndRound();
      }
    };
    tick();
    id = setInterval(tick, 1000);
    return () => { if (id) clearInterval(id); };
  }, [deadlineAtMs, timerDuration, timerKey, handleEndRound]);

  // Spy guess phase countdown timer
  useEffect(() => {
    if (!isSpyGuessPhase || !spyGuessDeadline) return;
    const tick = () => {
      const remaining = Math.ceil((spyGuessDeadline - Date.now()) / 1000);
      setSpyGuessTimeLeft(Math.max(0, remaining));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isSpyGuessPhase, spyGuessDeadline]);

  // Fetch server round start to compute deadline
  const syncDeadlineFromServer = useCallback(async () => {
    try {
      const res = await fetch(`/api/rooms/${roomId}`);
      if (!res.ok) return;
      const r = await res.json();
      if (r?.game_end_time) {
        // Clamp timer to ended state
        setTimeLeft(0);
        setDeadlineAtMs(Date.now());
        endRoundTriggeredRef.current = true;
        if (r?.round_scored) {
          setScoreUpdated(true); // suppress VotingPanel after scoring
        }
        return;
      }
      if (r?.round_started_at) {
        const startedAtMs = new Date(r.round_started_at).getTime();
        const endAt = startedAtMs + timerDuration * 1000;
        setDeadlineAtMs(endAt);
      }
    } catch {}
  }, [roomId, timerDuration]);

  // Helper function to create a hash of player data for change detection
  const createPlayerDataHash = useCallback((players: any[], currentDeck: string) => {
    const relevantData = players.map(p => ({
      id: p.player_id,
      role: p.role,
      is_spy: p.is_spy,
      game_location: p.game_location
    }));
    // Deterministic hash without timestamps to avoid false positives
    return JSON.stringify({ players: relevantData, deck: currentDeck });
  }, []);

  // Set up WebSocket connection and event handlers (converted from polling)
  useEffect(() => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 3;
    
    const connectWithRetry = () => {
      try {
        // Connect to WebSocket
        socketService.connect();
        socketService.joinRoom(roomId!, playerName, isAdmin, playerId);
        reconnectAttempts = 0; // Reset on successful connection
      } catch (error) {
        console.error('Socket connection failed:', error);
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          console.log(`Retrying socket connection (${reconnectAttempts}/${maxReconnectAttempts})...`);
          setTimeout(connectWithRetry, 1000 * reconnectAttempts); // Exponential backoff
        } else {
          console.error('Max socket reconnection attempts reached');
        }
      }
    };
    
    connectWithRetry();
    // On mount, sync timer deadline
    syncDeadlineFromServer();

    // Socket event handlers
    socketService.onPlayersUpdated((data: any) => {
      console.log('Players updated:', data);
      if (data?.players) {
        // Create hash of new data to detect meaningful changes
        const newHash = createPlayerDataHash(data.players, data.currentDeck || currentDeck);
        
        // Update players list for SuspectsBoard
        setPlayers(data.players.map((p: any) => ({
          id: p.player_id,
          name: p.player_name,
          iconId: p.icon_id || 0,
          score: p.score || 0
        })));
        
        // Handle deck synchronization (safety check during gameplay)
        if (data.currentDeck && data.currentDeck !== currentDeck) {
          console.warn('Deck mismatch detected in PlayGame! Expected:', currentDeck, 'Got:', data.currentDeck);
          // Log this for debugging but don't change the game state mid-game unless it's a restart
        }
        
        // Find current player's data using playerId (more reliable than playerName)
        const currentPlayer = data.players.find((p: any) => p.player_id === playerId);
        
        // Update current player's score for display in header
        if (currentPlayer) {
          setCurrentPlayerScore(currentPlayer.score || 0);
        }
        
        // Skip game restart detection during spy guess phase — the scoring
        // REST call emits players-updated which can race with the guess UI
        // and cause an unwanted navigate that resets component state.
        if (isSpyGuessPhaseRef.current) {
          return;
        }

        // Only process game restart if we have meaningful data changes and we're not already restarting
        if (currentPlayer && newHash !== lastPlayerUpdateHash && !isGameRestarting) {
          const hasRoleChanged = currentPlayer.role !== role;
          const hasSpyStatusChanged = currentPlayer.is_spy !== isSpy;
          const hasLocationChanged = currentPlayer.game_location !== gameLocation;
          const hasDeckChanged = data.currentDeck && data.currentDeck !== currentDeck;
          
          // Only restart if there are actual game-changing updates
          if (hasRoleChanged || hasSpyStatusChanged || hasLocationChanged || hasDeckChanged) {
            console.log('Game restart detected via player data change:', {
              hasRoleChanged,
              hasSpyStatusChanged,
              hasLocationChanged,
              hasDeckChanged,
              oldRole: role,
              newRole: currentPlayer.role,
              oldSpy: isSpy,
              newSpy: currentPlayer.is_spy
            });
            
            setIsGameRestarting(true);
            setLastPlayerUpdateHash(newHash);
            
            // Reset game state
            setTimeLeft(timerDuration);
            setProgress(100);
            setScoreUpdated(false);
            setCrossedOutCards({});
            setTimerKey(prev => prev + 1);
            setRoundKey(prev => prev + 1);
            setGameRestartId(prev => prev + 1);
            // Reset image loading state for new round
            setImageLoaded(false);
            setImageError(false);
            // Reset card flip state for new round
            setIsCardFlipped(false);
            // Clear any previous round voting tallies
            setVotingCounts({});
            setVotingVoters([]);
            setIsVotingClosed(false);
            setIsSpyGuessPhase(false);
            isSpyGuessPhaseRef.current = false;
            setSpyGuessDeadline(null);
            setHasSubmittedSpyGuess(false);
            setSpyGuessResult(null);
            endRoundTriggeredRef.current = false;

            // Navigate to the new game with updated roles
            navigate(`/app/play/${roomId}`, {
              state: {
                isAdmin,
                playerName,
                playerId,
                role: currentPlayer.role,
                location: currentPlayer.game_location,
                isSpy: currentPlayer.is_spy,
                chosenCards: chosenCards,
                timerDuration,
                iconId: currentPlayer.icon_id || iconId,
                currentDeck: data.currentDeck || currentDeck, // Use server deck or fallback to current
              },
              replace: true
            });
            
            // Reset restart flag after a short delay
            setTimeout(() => setIsGameRestarting(false), 1000);
          } else {
            // Update hash even if no restart needed to avoid repeated checks
            setLastPlayerUpdateHash(newHash);
          }
        }
      }
    });

    socketService.onRoundEnded(() => {
      console.log('Round ended via socket');
      setTimeLeft(0);
      setDeadlineAtMs(Date.now());
      endRoundTriggeredRef.current = true;
      // Increment roundKey to reset suspects board when round ends
      setRoundKey(prev => prev + 1);
    });

    socketService.onRoundActive((data: { roundStartedAt: number }) => {
      const endAt = data.roundStartedAt + timerDuration * 1000;
      setDeadlineAtMs(endAt);
    });

    socketService.onGameEnded(() => {
      console.log('Game ended via socket, navigating to winners podium');
      navigate(`/app/winners/${roomId}`, { state: { currentDeck } });
    });

    socketService.onGameRestarted(() => {
      console.log('Game restarted via socket - preparing for restart');
      // Set flag to indicate restart is coming - this will be handled by onPlayersUpdated
      setGameRestartId(prev => prev + 1);
      // Reset deadline immediately; onPlayersUpdated will navigate with new state
      setDeadlineAtMs(Date.now() + timerDuration * 1000);
      // Clear any previous round voting tallies proactively
      setVotingCounts({});
      setVotingVoters([]);
      setIsVotingClosed(false);
      setIsSpyGuessPhase(false);
      isSpyGuessPhaseRef.current = false;
      setSpyGuessDeadline(null);
      setHasSubmittedSpyGuess(false);
      setSpyGuessResult(null);
      endRoundTriggeredRef.current = false;
      setRoundResultText(null);
    });

    // Voting updates and completion — skip during spy guess phase to prevent stale events from disrupting UI
    socketService.onVotingUpdated((data: { counts: Record<string, number>; voters: string[]; isClosed?: boolean }) => {
      if (isSpyGuessPhaseRef.current) return;
      setVotingCounts(data?.counts || {});
      setVotingVoters(data?.voters || []);
      setIsVotingClosed(!!data?.isClosed);
    });

    socketService.onVotingComplete((data: { accusedId: string | null; isSpy: boolean; isUnanimous: boolean; spyGuessDeadline?: number }) => {
      setIsVotingClosed(true);
      setVotingCounts({});
      setVotingVoters([]);

      const spyCaught = data.isUnanimous && data.accusedId && data.isSpy;

      if (spyCaught && data.spyGuessDeadline) {
        // Spy caught — enter guess phase (server started a 60s timer)
        setIsSpyGuessPhase(true);
        isSpyGuessPhaseRef.current = true;
        setSpyGuessDeadline(data.spyGuessDeadline);
        setHasSubmittedSpyGuess(false);
        setSpyGuessResult(null);
      } else if (spyCaught) {
        handleRoundEnd('spy_caught');
        setRoundResultText(<><span>Round over. </span><span className="text-green-500">The {spyAssetNameRef.current} was caught.</span>{' '}</>);
      } else {
        handleRoundEnd('spy_escaped');
        (async () => {
          try {
            const res = await fetch(`/api/rooms/${roomId}/players`);
            if (res.ok) {
              const list = await res.json();
              const spy = Array.isArray(list) ? list.find((p: any) => p.is_spy) : null;
              if (spy && spy.player_name) {
                setRoundResultText(<><span>Round over. </span><span className="text-red-500">The {spyAssetNameRef.current} escaped, it was {spy.player_name}.</span>{' '}</>);
              } else {
                setRoundResultText(<><span>Round over. </span><span className="text-red-500">The {spyAssetNameRef.current} escaped.</span>{' '}</>);
              }
            } else {
              setRoundResultText(<><span>Round over. </span><span className="text-red-500">The {spyAssetNameRef.current} escaped.</span>{' '}</>);
            }
          } catch {
            setRoundResultText(<><span>Round over. </span><span className="text-red-500">The {spyAssetNameRef.current} escaped.</span>{' '}</>);
          }
        })();
      }
    });

    socketService.onVotingEndedManually(() => {
      setIsVotingClosed(true);
    });

    socketService.onSpyGuessActive((data: { spyGuessDeadline: number }) => {
      setIsSpyGuessPhase(true);
      isSpyGuessPhaseRef.current = true;
      setSpyGuessDeadline(data.spyGuessDeadline);
      setHasSubmittedSpyGuess(false);
      setSpyGuessResult(null);
    });

    socketService.onSpyGuessResult((data: { correct: boolean; guessedLocation: string | null; actualLocation: string }) => {
      setSpyGuessResult(data);
      if (data.correct) {
        handleRoundEnd('spy_caught_location');
        setRoundResultText(<><span>Round over. </span><span className="text-yellow-500">The {spyAssetNameRef.current} was caught but guessed the {cardNounRef.current.toLowerCase()} correctly! They get +1 point.</span>{' '}</>);
      } else if (data.guessedLocation) {
        handleRoundEnd('spy_caught');
        setRoundResultText(<><span>Round over. </span><span className="text-green-500">The {spyAssetNameRef.current} was caught and guessed incorrectly!</span>{' '}</>);
      } else {
        handleRoundEnd('spy_caught');
        setRoundResultText(<><span>Round over. </span><span className="text-green-500">The {spyAssetNameRef.current} was caught and ran out of time!</span>{' '}</>);
      }
    });

    socketService.onScoresUpdated((data?: { outcome?: 'spy_escaped' | 'spy_caught_location' | 'spy_caught' }) => {
      setScoreUpdated(true);
      setIsVotingClosed(true);
      setVotingCounts({});
      setVotingVoters([]);

      if (data?.outcome === 'spy_caught') {
        setRoundResultText(<><span>Round over. </span><span className="text-green-500">The {spyAssetNameRef.current} was caught.</span>{' '}</>);
      } else if (data?.outcome === 'spy_escaped') {
        setRoundResultText(<><span>Round over. </span><span className="text-red-500">The {spyAssetNameRef.current} escaped.</span>{' '}</>);
      }
    });

    // Handle socket disconnection with reconnect logic
    const handleSocketError = (error: any) => {
      console.error('Socket error:', error);
      // Try to reconnect if we lose connection during gameplay
      if (!socketService.isConnected() && reconnectAttempts < maxReconnectAttempts) {
        console.log('Attempting to reconnect after socket error...');
        setTimeout(connectWithRetry, 2000);
      }
    };
    
    socketService.onError(handleSocketError);

    // Cleanup on unmount
    return () => {
      socketService.removeAllListeners();
      socketService.disconnect();
      // Clear any pending fallback timeout
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally excluding role, isSpy, chosenCards, timerDuration, iconId, navigate to prevent socket reconnections during game state changes
  }, [roomId, playerName, isAdmin, playerId, createPlayerDataHash]);

  // Resume on visibility/focus: reconnect, rejoin, and sync room status
  useEffect(() => {
    const resume = async () => {
      // During spy guess phase, only reconnect the socket — don't sync state
      // that could disrupt the guess UI
      if (isSpyGuessPhaseRef.current) {
        socketService.resume();
        return;
      }
      socketService.resume();
      await syncDeadlineFromServer();
      try {
        const res = await fetch(`/api/rooms/${roomId}`);
        if (res.ok) {
          const r = await res.json();
          if (r.game_ended) {
            navigate(`/app/winners/${roomId}`, { state: { currentDeck } });
            return;
          }
          if (r.game_end_time) {
            setTimeLeft(0);
            setDeadlineAtMs(Date.now());
            endRoundTriggeredRef.current = true;
          }
        }
      } catch (e) {
        // best-effort
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') resume();
    };

    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', resume);
    window.addEventListener('online', resume);

    return () => {
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', resume);
      window.removeEventListener('online', resume);
    };
  }, [roomId, navigate, currentDeck, syncDeadlineFromServer]);

  // Request voting state once when round ends (not during every render)
  useEffect(() => {
    if (timeLeft === 0 && !scoreUpdated && !isSpyGuessPhase) {
      socketService.requestVotingState();
    }
  }, [timeLeft, scoreUpdated, isSpyGuessPhase]);

  const handleRoundEnd = async (outcome: 'spy_escaped' | 'spy_caught_location' | 'spy_caught') => {
    try {
      // Keep as REST for critical score updates
      const response = await fetch(`/api/rooms/${roomId}/game-round-end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ outcome }),
      });

      if (!response.ok) {
        throw new Error('Failed to update scores');
      }

      // Set score updated to true after successful update
      setScoreUpdated(true);
    } catch (error) {
      console.error('Error updating scores:', error);
      // Still set scoreUpdated to true to allow user to proceed
      setScoreUpdated(true);
    }
  };

  const handleSpyGuess = useCallback((guessedLocation: string) => {
    if (hasSubmittedSpyGuess) return;
    setHasSubmittedSpyGuess(true);
    socketService.submitSpyGuess(playerId, guessedLocation);
  }, [playerId, hasSubmittedSpyGuess]);

  const handleEndVoting = useCallback(() => {
    try {
      socketService.endVoting();
      setIsVotingClosed(true);
    } catch (e) {
      // best-effort
    }
  }, []);

  const handleManualOutcome = async (outcome: 'spy_escaped' | 'spy_caught' | 'spy_caught_location') => {
    if (isSubmittingManualOutcome) return;
    setIsSubmittingManualOutcome(true);
    try {
      await handleRoundEnd(outcome);
      if (outcome === 'spy_caught') {
        setRoundResultText(<><span>Round over. </span><span className="text-green-500">The {spyAssetNameRef.current} was caught.</span>{' '}</>);
      } else if (outcome === 'spy_caught_location') {
        setRoundResultText(<><span>Round over. </span><span className="text-yellow-500">The {spyAssetNameRef.current} was caught but guessed the {cardNoun.toLowerCase()} correctly!</span>{' '}</>);
      } else {
        setRoundResultText(<><span>Round over. </span><span className="text-red-500">The {spyAssetNameRef.current} escaped.</span>{' '}</>);
      }
    } finally {
      setIsSubmittingManualOutcome(false);
    }
  };

  const handleEndGame = async () => {
    try {
      console.log('Ending game via socket...');
      // Convert to socket event (pure socket approach)
      socketService.endGame();
      // Navigation will happen via socket event
    } catch (error) {
      console.error('Error ending game:', error);
      alert('Failed to end game. See console for details.');
    }
  };



  const handleOneMoreRound = async () => {
    try {
      console.log('Restarting game via REST API...');
      setIsGameRestarting(true); // Prevent multiple restart attempts
      
      // Keep as hybrid (REST + Socket broadcast)
      const response = await fetch(`/api/rooms/${roomId}/restart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to restart game: ${response.status} ${errorText}`);
      }

      console.log('Game restart successful');
      // Game state reset and navigation will happen via socket events
      // Deadline will be updated on socket events; ensure a fallback here
      setDeadlineAtMs(Date.now() + timerDuration * 1000);
      // Reset the restart flag after a timeout in case socket events don't arrive
      setTimeout(() => {
        if (isGameRestarting) {
          console.warn('Game restart timeout - resetting flag');
          setIsGameRestarting(false);
        }
      }, 5000);
    } catch (error) {
      console.error('Error restarting game:', error);
      setIsGameRestarting(false);
      alert('Failed to restart game. Please try again or return to home.');
      // Don't automatically navigate away - let user decide
    }
  };

  const getRandomQuestion = () => {
    const randomQuestion = getRandomQuestionForDeck(currentDeck);
    setCurrentQuestion(randomQuestion);
  };

  const handleVote = useCallback((targetId: string) => {
    try {
      socketService.castVote(playerId, targetId);
    } catch (e) {
      // best-effort
    }
  }, [playerId]);

  // Handle card flip
  const handleCardFlip = () => {
    setIsCardFlipped(!isCardFlipped);
  };

  // Safety check - if currentDeck is missing, show error
  if (!currentDeck) {
    console.error('PlayGame: currentDeck is missing from location.state');
    return (
      <div className="fixed inset-0 bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Game Error</h1>
          <p className="text-gray-300 mb-4">Missing game deck information</p>
          <Button onClick={() => navigate('/app')}>Return to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col overflow-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-gray-800 bg-black/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xl font-bold text-red-500">SPYFALL</span>
            </button>
          </div>
          <Button 
            variant="outline" 
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            onClick={handleNewGame}
          >
            New Game
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full py-8">
        {/* Main container with vertical ads */}
        <div className="flex justify-center gap-4 max-w-[1600px] mx-auto px-4">
          {/* Left Vertical Ad - Hidden on mobile and tablet */}
          <div className="hidden xl:flex flex-col items-center sticky top-20 h-fit">
             <GoogleAdVertical/> 
            {/* Additional vertical ad space if needed */}
            {/* <GoogleAdVertical /> */}
          </div>

          {/* Main Content */}
          <div className="flex-1 max-w-6xl">
            <div className="flex flex-col gap-4 mb-8">
              <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold">Game in Progress</h1>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                      <div 
                        dangerouslySetInnerHTML={{ __html: getPlayerIconById(iconId).svg }} 
                        className="w-6 h-6" 
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-300 font-medium">{playerName}</span>
                      <span className="text-xs text-green-400 font-mono">Score: {currentPlayerScore}</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">Room: <span className="animate-text bg-gradient-to-r from-red-800 via-red-500 to-red-300 bg-clip-text text-transparent font-medium">{roomId}</span></div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {timeLeft > 0 ? (
                  <>
                    <div className="text-2xl font-mono font-bold text-red-500">
                      {formatTime(timeLeft)}
                    </div>
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 transition-all duration-1000 ease-linear"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        onClick={handleEndRound}
                      >
                        End Round
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center gap-4">
                    <div className="text-3xl font-bold text-red-500">Round Over</div>
                    {!scoreUpdated ? (
                      <div className="flex flex-col gap-4 w-full">
                        {isSpyGuessPhase ? (
                          <SpyGuessPanel
                            isSpy={isSpy}
                            chosenCards={chosenCards}
                            spyAssetName={resolvedSpyAssetName}
                            cardNoun={cardNoun}
                            spyGuessTimeLeft={spyGuessTimeLeft}
                            hasSubmittedGuess={hasSubmittedSpyGuess}
                            guessResult={spyGuessResult}
                            onGuess={handleSpyGuess}
                            getImagePath={getImagePath}
                          />
                        ) : !isVotingClosed ? (
                          <>
                            {isAdmin && (
                              <div className="flex justify-center">
                                <Button
                                  variant="outline"
                                  className="border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black"
                                  onClick={handleEndVoting}
                                >
                                  (Admin only) Force Voting End
                                </Button>
                              </div>
                            )}
                            <VotingPanel 
                              roomId={roomId!}
                              players={players.map(p => ({ id: p.id, name: p.name, iconId: p.iconId }))}
                              myPlayerId={playerId}
                              currentDeck={currentDeck}
                              spyAssetName={resolvedSpyAssetName}
                              onVote={handleVote}
                              counts={votingCounts}
                              voters={votingVoters}
                            />
                          </>
                        ) : (
                          isAdmin ? (
                            <div className="flex flex-col gap-3 w-full max-w-md items-center self-center">
                              <div className="text-gray-300 text-center">
                                Voting has ended. Choose the round outcome.
                              </div>
                              <Button
                                variant="ghost"
                                className="bg-red-600 hover:bg-red-700 w-full"
                                onClick={() => handleManualOutcome('spy_escaped')}
                                disabled={isSubmittingManualOutcome}
                              >
                                {resolvedSpyAssetName.charAt(0).toUpperCase() + resolvedSpyAssetName.slice(1)} Escaped (+2 to {resolvedSpyAssetName})
                              </Button>
                              <Button
                                variant="ghost"
                                className="bg-green-600 hover:bg-green-700 w-full"
                                onClick={() => handleManualOutcome('spy_caught')}
                                disabled={isSubmittingManualOutcome}
                              >
                                {resolvedSpyAssetName.charAt(0).toUpperCase() + resolvedSpyAssetName.slice(1)} Caught (+1 to non-{resolvedSpyAssetName}s)
                              </Button>
                              <Button
                                variant="ghost"
                                className="bg-yellow-600 hover:bg-yellow-700 w-full"
                                onClick={() => handleManualOutcome('spy_caught_location')}
                                disabled={isSubmittingManualOutcome}
                              >
                                {resolvedSpyAssetName.charAt(0).toUpperCase() + resolvedSpyAssetName.slice(1)} Caught - {cardNoun} Guessed (+1 to {resolvedSpyAssetName})
                              </Button>
                            </div>
                          ) : (
                            <div className="text-gray-400 text-center">
                              Voting has ended. Waiting for admin to assign points...
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      isAdmin ? (
                        <div className="flex flex-col gap-2 w-full max-w-md items-center text-center">
                          {roundResultText && (
                            <div className="text-gray-300">
                              {roundResultText}
                              <br />
                              Waiting for admin to start a new round...
                            </div>
                          )}
                          <Button
                            className="bg-green-600 hover:bg-green-700 w-full"
                            onClick={handleOneMoreRound}
                            disabled={isGameRestarting}
                          >
                            {isGameRestarting ? 'Starting New Round...' : 'One More Round'}
                          </Button>
                          <Button
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white w-full"
                            onClick={handleEndGame}
                          >
                            End Game
                          </Button>
                        </div>
                      ) : (
                        <div className="text-gray-400 text-center">
                          {roundResultText ? <>
                            {roundResultText}
                            <br />
                            Waiting for admin to start a new round...
                          </> : "Waiting for admin to start a new round..."}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
          {/* Player's card */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Your {isSpy ? 'Mission' : cardNoun}</h2>
            {showMobileHint && (
              <div className="md:hidden text-blue-400 text-sm -mt-1 text-center">
                Pull down to refresh if your device disconnects
              </div>
            )}
            <div 
              className="relative aspect-[3/4] cursor-pointer hover:scale-105 transition-transform duration-200"
              style={{ perspective: '1000px' }}
              onClick={handleCardFlip}
              title="Click to flip card"
            >
              <div 
                className={`relative w-full h-full transition-transform duration-700 preserve-3d ${isCardFlipped ? 'rotate-y-180' : ''}`}
                style={{ 
                  transformStyle: 'preserve-3d',
                  transform: isCardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}
              >
                {/* Front of card */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-red-900 to-red-700 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center border-2 border-red-500 overflow-hidden group backface-hidden"
                  style={{ backfaceVisibility: 'hidden' }}
                >
              <div className="absolute inset-0 transition-transform duration-300 group-hover:scale-110">
                              <img
                key={`${gameLocation}-${currentDeck}-${gameRestartId}`} // Force re-render with unique key based on game restart
                src={isSpy ? getImagePath('spy', false) : getImagePath(gameLocation, true)}
                alt={isSpy ? (resolvedSpyAssetName.charAt(0).toUpperCase() + resolvedSpyAssetName.slice(1)) : gameLocation}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  imageLoaded && !imageError ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={handleImageLoad}
                onError={handleImageLoadError}
              />
                {/* Loading placeholder */}
                {(!imageLoaded && !imageError) && (
                  <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
                    <div className="text-gray-400 text-sm">Loading...</div>
                  </div>
                )}
                {/* Error fallback */}
                {imageError && (
                  <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                    <div className="text-gray-400 text-sm text-center">
                      <div className="mb-2">Image not available</div>
                      <div className="text-xs opacity-75">
                        {isSpy ? (resolvedSpyAssetName.charAt(0).toUpperCase() + resolvedSpyAssetName.slice(1)) : gameLocation}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="absolute inset-0 bg-black/20 transition-opacity duration-300 group-hover:bg-black/10" />
              <div className="absolute inset-[3px] bg-gray-900/50 rounded-md flex flex-col items-center justify-center p-4">
                {isSpy ? (
                  <>
                    <div className="text-red-500 font-bold text-lg mb-2">
                      YOU ARE THE {resolvedSpyAssetName.toUpperCase()}!
                    </div>
                    <div className="text-2xl font-bold">
                      Find the {cardNoun}
                    </div>
                    <div className="mt-4 text-sm text-gray-400">
                      Listen carefully to other players&apos; conversations to figure out the {cardNoun.toLowerCase()}.
                    </div>
                    <div className="mt-4 text-sm text-red-400">
                      Click to hide your card.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-red-500 font-bold text-lg mb-2">
                      {cardNoun.toUpperCase()}
                    </div>
                    <div className="text-2xl font-bold">{gameLocation}</div>
                    <div className="mt-2 text-lg text-gray-300">Role: {role}</div>
                    <div className="mt-4 text-sm text-gray-400">
                      Remember: Don&apos;t reveal your {cardNoun.toLowerCase()}! Ask and answer questions carefully.
                    </div>
                    <div className="mt-4 text-sm text-red-400">
                      Click to hide your card.
                    </div>
                  </>
                )}
              </div>
                </div>
                
                {/* Back of card */}
                <div 
                  className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-700 rounded-lg shadow-lg border-2 border-gray-500 overflow-hidden backface-hidden"
                  style={{ 
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)'
                  }}
                >
                  <div className="absolute inset-0">
                    <img
                      src="/imgs/spys/locationHidden.webp"
                      alt="Hidden Location"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-[3px] flex flex-col items-center justify-center p-4">
            
        
                  </div>
                </div>
              </div>
            </div>
            
            {/* Square Ad below main card */}
            <GoogleAdSquare/>
            
            {/* Game Rules */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Game Rules</h3>
              <div className="overflow-hidden transition-all duration-300 ease-in-out" style={{ maxHeight: isRulesExpanded ? '1000px' : '120px' }}>
                <ul className="text-sm text-gray-300 space-y-1">
                  {[
                    `If you're a regular player, your job is to figure out who the ${resolvedSpyAssetName} is`,
                    `If you're the ${resolvedSpyAssetName}, your mission is to figure out the ${currentDeck === 'animalsDeck' ? 'animal' : 'location'} (and not get caught)`,
                    `You can pick one player at random and ask them a question regarding the ${currentDeck === 'animalsDeck' ? 'animal' : 'location'} (They answer)`,
                    `Then this player can pick another player at random and ask them a question regarding the ${currentDeck === 'animalsDeck' ? 'animal' : 'location'}`,
                    `Repeat untill everyone is ready to accuse a player of being the ${resolvedSpyAssetName}`,
                    `At any point in the game, if the ${resolvedSpyAssetName} knows the ${currentDeck === 'animalsDeck' ? 'animal' : 'location'}, they can reveal the ${currentDeck === 'animalsDeck' ? 'animal' : 'location'} and win the round.`,
                    `Unaimously pick a player who you belive is the ${resolvedSpyAssetName} (Before the time runs out)`,
                    `${resolvedSpyAssetName.charAt(0).toUpperCase() + resolvedSpyAssetName.slice(1)} wins the game if they have not been caught by the end of the timer (2 points)`,
                    `If you catch the ${resolvedSpyAssetName}, but they can guess the ${currentDeck === 'animalsDeck' ? 'animal' : 'location'} the ${resolvedSpyAssetName} gets (1 point)`,
                    `If the ${resolvedSpyAssetName} is caught, the ${resolvedSpyAssetName} gets 0 points, the team gets (1 point each)`
                  ].map((rule, index) => (
                    <li key={index}>• {rule}</li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setIsRulesExpanded(!isRulesExpanded)}
                className="mt-2 text-sm text-red-500 hover:text-red-400 transition-colors flex items-center gap-1"
              >
                {isRulesExpanded ? (
                  <>
                    Show Less
                    <svg className="w-4 h-4 transform transition-transform duration-300" style={{ transform: isRulesExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </>
                ) : (
                  <>
                    Read More
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </>
                )}
              </button>
              <div className="mt-4 space-y-4">
                <Button
                  variant="outline"
                  className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  onClick={getRandomQuestion}
                >
                  Help me with a question
                </Button>
                {currentQuestion && (
                  <div className="p-3 bg-gray-700 rounded-md text-sm text-gray-200">
                    {currentQuestion}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Conditional rendering based on player role if spy they see the location cards else they see the suspects board*/}
          <div>
            {isSpy ? (
              <>
                {/* <GoogleAdHorizontalFixed/> */}
                <LocationCards 
                  locations={chosenCards} 
                  crossedOutCards={crossedOutCards} 
                  toggleCard={toggleCard}
                  isSpy={true}
                  currentDeck={currentDeck}
                />
              </>
            ) : (
              <>
                {/* <GoogleAdHorizontalFixed/> */}
                <SuspectsBoard 
                  players={players}
                  locations={chosenCards}
                  roundKey={roundKey}
                  currentDeck={currentDeck}
                />
              </>
            )}
          </div>
        </div>
          </div>

          {/* Right Vertical Ad - Hidden on mobile and tablet */}
          <div className="hidden xl:flex flex-col items-center sticky top-20 h-fit">
            <GoogleAdVertical />
            {/* Additional vertical ad space if needed */}
            {/* <GoogleAdVertical />*/}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}


